import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { logActivity, ActivityActions, Modules } from '@/lib/activity-logger';

interface AuthStore {
  isLoggedIn: boolean;
  user: {
    id: string;
    nama: string;
    role: string;
  } | null;
}

// Simple auth store (in production, use proper authentication)
const getAuthState = (): AuthStore => {
  const stored = localStorage.getItem('auth_state');
  return stored ? JSON.parse(stored) : { isLoggedIn: false, user: null };
};

const setAuthState = (auth: AuthStore) => {
  localStorage.setItem('auth_state', JSON.stringify(auth));
};

// Default admin credentials (change these in production!)
const ADMIN_USERS = [
  { userId: 'admin', password: 'admin123', nama: 'Administrator', role: 'Admin' },
  { userId: 'pentadbir', password: 'pentadbir123', nama: 'Pentadbir SAP', role: 'Pentadbir' },
  { userId: 'staff', password: 'staff123', nama: 'Staff', role: 'Staff' },
];

const Login = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Simulate authentication delay
    setTimeout(() => {
      const user = ADMIN_USERS.find(u => u.userId === userId && u.password === password);
      
      if (user) {
        const authState: AuthStore = {
          isLoggedIn: true,
          user: {
            id: user.userId,
            nama: user.nama,
            role: user.role,
          },
        };
        setAuthState(authState);
        
        // Log login activity
        logActivity(
          user.userId,
          user.nama,
          ActivityActions.LOGIN,
          Modules.AUTH,
          `${user.nama} (${user.role}) logged in successfully`
        );
        
        toast.success(`Selamat datang, ${user.nama}!`);
        navigate('/');
      } else {
        setError('User ID atau Password salah!');
        
        // Log failed login attempt
        logActivity(
          'unknown',
          'Unknown User',
          'LOGIN_FAILED',
          Modules.AUTH,
          `Failed login attempt with ID: ${userId}`
        );
        
        toast.error('Login gagal. Periksa User ID dan Password Anda.');
      }
      setLoading(false);
    }, 500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <div className="text-2xl font-bold text-slate-900">SAP</div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">SAP Ngecat</h1>
          <p className="text-slate-400">Sistem Pengurusan Projek</p>
        </div>

        {/* Login Card */}
        <Card className="border-0 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg">
            <CardTitle className="text-2xl">Login</CardTitle>
            <CardDescription className="text-red-100">Masukkan User ID dan Password</CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
                  <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="text-sm font-medium text-red-800">{error}</p>
                  </div>
                </div>
              )}

              {/* User ID Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">User ID</label>
                <Input
                  type="text"
                  placeholder="Masukkan User ID"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  disabled={loading}
                  className="border-slate-300 focus:border-red-600 focus:ring-red-600"
                  autoFocus
                />
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Password</label>
                <Input
                  type="password"
                  placeholder="Masukkan Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className="border-slate-300 focus:border-red-600 focus:ring-red-600"
                />
              </div>

              {/* Login Button */}
              <Button
                type="submit"
                disabled={loading || !userId || !password}
                className="w-full bg-red-600 hover:bg-red-700 h-10 text-base font-medium"
              >
                {loading ? 'Sedang Login...' : 'Login'}
              </Button>
            </form>

            {/* Demo Credentials */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-xs font-semibold text-slate-700 mb-3 uppercase">Demo Credentials</p>
              <div className="space-y-2 text-xs">
                <div className="bg-slate-50 p-2 rounded">
                  <p className="font-medium text-slate-900">Admin:</p>
                  <p className="text-slate-600">ID: <code className="bg-white px-1 rounded">admin</code> | Pass: <code className="bg-white px-1 rounded">admin123</code></p>
                </div>
                <div className="bg-slate-50 p-2 rounded">
                  <p className="font-medium text-slate-900">Pentadbir:</p>
                  <p className="text-slate-600">ID: <code className="bg-white px-1 rounded">pentadbir</code> | Pass: <code className="bg-white px-1 rounded">pentadbir123</code></p>
                </div>
                <div className="bg-slate-50 p-2 rounded">
                  <p className="font-medium text-slate-900">Staff:</p>
                  <p className="text-slate-600">ID: <code className="bg-white px-1 rounded">staff</code> | Pass: <code className="bg-white px-1 rounded">staff123</code></p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer Info */}
        <div className="mt-8 text-center text-slate-400 text-sm">
          <p>SAP Ngecat SERVICES</p>
          <p>016 253 1044 | sapngecat2003@gmail.com</p>
        </div>
      </div>
    </div>
  );
};

export { getAuthState, setAuthState, type AuthStore };
export default Login;